import React, { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { Button } from "@/components/ui/button"
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Switch } from "@/components/ui/switch"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { CalendarIcon, MapPin, Link, Search, AlertCircle } from "lucide-react"
import { format } from "date-fns"
import { useToast } from "@/hooks/use-toast"
import { useAuth } from "@/contexts/auth-context"
import eventService from "@/services/eventService"
import { ReactQuillEditor } from "@/components/ui/react-quill-editor"
import EventSessions from "./event-sessions"
import ReminderTimeConfig from "./reminder-time-config"
import { formService } from "@/services/formService"
import { RegistrationForm } from "@/types/form-types"
import { UserService } from "@/services/user-service"
import {
  ZubinEvent,
  eventCategories,
  targetGroups,
  eventStatuses,
  hongKongDistricts,
} from "@/types/event-types"
import { Label } from "@/components/ui/label"

// Define the form schema
const eventFormSchema = z.object({
  title: z.string().min(2, {
    message: "Event title is required and must be at least 2 characters.",
  }),
  description: z.string().min(1, {
    message: "Event description is required.",
  }),
  category: z.string().min(1, {
    message: "Category is required.",
  }),
  targetGroup: z.string().min(1, {
    message: "Target group is required.",
  }),
  location: z
    .object({
      venue: z.string().optional(),
      address: z.string().optional(),
      district: z.string().optional(),
      onlineEvent: z.boolean(),
      meetingLink: z.string().optional(),
    })
    .refine(
      (loc) => {
        if (loc.onlineEvent) {
          return !!loc.meetingLink?.trim();
        }
        return (
          (loc.venue?.length ?? 0) >= 2 &&
          (loc.address?.length ?? 0) >= 2 &&
          (loc.district?.length ?? 0) >= 1
        );
      },
      { message: "For online events provide a meeting link; for in-person events fill venue, address, and district." }
    ),
  startDate: z.date({
    required_error: "Start date is required.",
  }),
  endDate: z.date({
    required_error: "End date is required.",
  }),
  isPrivate: z.boolean(),
  status: z.enum(["Draft", "Published", "Cancelled", "Completed"]),
  registrationFormId: z.string().min(1, {
    message: "Registration form is required.",
  }),
  sessions: z.array(
    z.object({
      _id: z.string(),
      title: z.string().min(1, "Session title is required"),
      description: z.string().optional(),
      date: z.date(),
      startTime: z.string().min(1, "Start time is required"),
      endTime: z.string().min(1, "End time is required"),
      location: z
        .object({
          venue: z.string().optional(),
          meetingLink: z.string().optional(),
        })
        .optional(),
      capacity: z.number().optional(),
    })
  ),
  capacity: z.number().optional(),
  tags: z.array(z.string()).optional(),
  reminderTimes: z.array(z.number()).optional(),
  defaultReminderMode: z.literal('template'),
  reminderRemarks: z.string().max(200, "Remarks cannot exceed 200 characters").optional(),
  customReminderTemplateSid: z.string().optional(),
  staffContact: z.object({
    name: z.string().optional(),
    phone: z.string().optional(),
  }).optional(),
  participants: z.array(z.string()).optional(),
})

type EventFormValues = z.infer<typeof eventFormSchema>

interface NewEventBuilderProps {
  onClose: () => void
  onSave: (data: any) => void
  eventId?: string | null
  defaultValues?: Partial<ZubinEvent>
}

export default function NewEventBuilder({ onClose, onSave, eventId, defaultValues }: NewEventBuilderProps) {
  const [activeTab, setActiveTab] = useState("basic")
  const [previewImage, setPreviewImage] = useState<string | null>(null)
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [registrationForms, setRegistrationForms] = useState<RegistrationForm[]>([])
  const [loadingForms, setLoadingForms] = useState(true)
  const [shouldRemoveImage, setShouldRemoveImage] = useState(false)
  const [selectedFileInfo, setSelectedFileInfo] = useState<{ name: string; size: string } | null>(null)
  const [availableUsers, setAvailableUsers] = useState<Array<{_id: string, firstName: string, lastName: string, email?: string, role: string}>>([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitErrorDialog, setSubmitErrorDialog] = useState<{ open: boolean; title: string; message: string; details?: string }>({ open: false, title: "", message: "" });
  const { toast } = useToast()
  const { user } = useAuth()

  // Format today's date for reference
  const today = new Date()

  // Fetch registration forms on component mount
  useEffect(() => {
    const fetchForms = async () => {
      try {
        setLoadingForms(true)
        const forms = await formService.getAllForms()
        setRegistrationForms(forms)
      } catch (error) {
        console.error("Error fetching registration forms:", error)
        toast({
          title: "Error",
          description: "Failed to load registration forms",
          variant: "destructive",
        })
      } finally {
        setLoadingForms(false)
      }
    }

    fetchForms()
  }, [toast])

  // Fetch available users for participant selection
  useEffect(() => {
    const fetchAvailableUsers = async () => {
      try {
        setLoadingUsers(true);
        const userService = UserService.getInstance();
        
        if (eventId) {
          // For existing events, use the event-specific endpoint
          const users = await eventService.getAvailableUsers(eventId);
          setAvailableUsers(users);
        } else {
          // For new events, fetch all users using the user service
          const allUsers = await userService.getAllUsers();
          // Transform to match the expected format
          const transformedUsers = allUsers.map(user => ({
            _id: user._id,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            role: user.role
          }));
          setAvailableUsers(transformedUsers);
        }
      } catch (error) {
        console.error("Error fetching available users:", error);
        // Don't show error toast for this as it's not critical
        setAvailableUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchAvailableUsers();
  }, [eventId]);

  // Initialize the form
  const form = useForm<EventFormValues>({
    resolver: zodResolver(eventFormSchema),
    defaultValues: {
      title: "",
      description: "",
      category: "",
      targetGroup: "",
      location: {
        venue: "",
        address: "",
        district: "",
        onlineEvent: false,
      },
      startDate: today,
      endDate: today,
      isPrivate: false,
      status: "Draft",
      registrationFormId: "",
      sessions: [],
      capacity: undefined,
      tags: [],
      reminderTimes: [24], // Default to 24 hours before event
      defaultReminderMode: 'template',
      reminderRemarks: "",
      customReminderTemplateSid: "",
      staffContact: {
        name: "",
        phone: "",
      },
      participants: [],
    },
  })

  // Watch startDate so the End Date min attribute stays reactive
  const watchedStartDate = form.watch("startDate");

  // Fetch event data when editing
  useEffect(() => {
    const fetchEventData = async () => {
      if (eventId && !defaultValues) {
        try {
          setIsSubmitting(true)
          const eventData = await eventService.getEvent(eventId)

          // Check if staff user is authorized to edit this event
          const createdById =
            typeof eventData.createdBy === "object" && eventData.createdBy !== null
              ? (eventData.createdBy as any)._id || (eventData.createdBy as any).id
              : eventData.createdBy
          if (user?.role === "staff" && createdById && createdById !== user._id) {
            toast({
              title: "Access Denied",
              description: "Staff members can only edit events they have created.",
              variant: "destructive",
            })
            onClose()
            return
          }
          
          // Transform the event data to match form structure
          const formData = {
            title: eventData.title,
            description: eventData.description,
            category: eventData.category,
            targetGroup: eventData.targetGroup,
            location: eventData.location,
            startDate: new Date(eventData.startDate),
            endDate: new Date(eventData.endDate),
            isPrivate: eventData.isPrivate,
            status: eventData.status,
            registrationFormId: eventData.registrationFormId,
            sessions: eventData.sessions.map((session: any) => ({
              _id: session._id,
              title: session.title,
              description: session.description,
              date: new Date(session.date),
              startTime: session.startTime,
              endTime: session.endTime,
              location: session.location,
              capacity: session.capacity
            })),
            capacity: eventData.capacity,
            tags: eventData.tags || [],
            reminderTimes: eventData.reminderTimes || [24],
            defaultReminderMode: 'template' as const,
            reminderRemarks: eventData.reminderRemarks || "",
            customReminderTemplateSid: eventData.customReminderTemplateSid || "",
            staffContact: eventData.staffContact || {
              name: "",
              phone: "",
            },
            participants: Array.isArray(eventData.participants) 
              ? eventData.participants.map((p: any) => {
                  if (typeof p === 'string') return p;
                  return p?._id || p?.id || '';
                })
              : [],
          }

          console.log('Event data from API:', eventData);
          console.log('Staff contact from API:', eventData.staffContact);
          console.log('Form data staff contact:', formData.staffContact);

          // Reset form with the fetched data
          form.reset(formData)
          
          // Force a small delay to ensure form fields are properly updated
          setTimeout(() => {
            // Trigger form validation to ensure all fields are properly rendered
            form.trigger()
          }, 100)
          
          // Set preview image if exists
          if (eventData.coverImage?.data) {
            const imageUrl = eventService.getEventImageUrl(eventId, eventData)
            if (imageUrl) {
              setPreviewImage(imageUrl)
            }
          }
          
          // Reset image removal flag when loading existing event
          setShouldRemoveImage(false)
          setSelectedFileInfo(null) // Reset file info when loading existing event
        } catch (error) {
          console.error("Error fetching event data:", error)
          toast({
            title: "Error",
            description: "Failed to load event data for editing",
            variant: "destructive",
          })
        } finally {
          setIsSubmitting(false)
        }
      }
    }

    fetchEventData()
  }, [eventId, defaultValues, form, toast])

  // Handle form submission
  async function onSubmit(data: EventFormValues) {
    try {
      setIsSubmitting(true)

      // Validate date logic
      if (data.startDate > data.endDate) {
        toast({
          title: "Invalid Date Range",
          description: "End date must be after start date. Please adjust the event dates.",
          variant: "destructive"
        })
        setIsSubmitting(false)
        return
      }

      // Check if user is authenticated
      if (!user) {
        toast({
          title: "Authentication Required",
          description: "Please log in to create or update events.",
          variant: "destructive"
        })
        setIsSubmitting(false)
        return
      }

      // Create FormData for multipart/form-data submission
      const formData = new FormData()
      
      // Add all event data
      formData.append('title', data.title)
      formData.append('description', data.description)
      formData.append('category', data.category)
      formData.append('targetGroup', data.targetGroup)
      formData.append('location[venue]', data.location.venue || '')
      formData.append('location[address]', data.location.address || '')
      formData.append('location[district]', data.location.district || '')
      formData.append('location[onlineEvent]', data.location.onlineEvent.toString())
      if (data.location.meetingLink) {
        formData.append('location[meetingLink]', data.location.meetingLink)
      }
      formData.append('startDate', data.startDate.toISOString())
      formData.append('endDate', data.endDate.toISOString())
      formData.append('isPrivate', data.isPrivate.toString())
      formData.append('status', data.status)
      formData.append('registrationFormId', data.registrationFormId)
      
      // Add sessions
      data.sessions.forEach((session, index) => {
        formData.append(`sessions[${index}][title]`, session.title)
        if (session.description) {
          formData.append(`sessions[${index}][description]`, session.description)
        }
        formData.append(`sessions[${index}][date]`, format(session.date, 'yyyy-MM-dd'))
        formData.append(`sessions[${index}][startTime]`, session.startTime)
        formData.append(`sessions[${index}][endTime]`, session.endTime)
        if (session.location?.venue) {
          formData.append(`sessions[${index}][location][venue]`, session.location.venue)
        }
        if (session.location?.meetingLink) {
          formData.append(`sessions[${index}][location][meetingLink]`, session.location.meetingLink)
        }
        if (session.capacity) {
          formData.append(`sessions[${index}][capacity]`, session.capacity.toString())
        }
      })
      
      if (data.capacity) {
        formData.append('capacity', data.capacity.toString())
      }
      
      if (data.tags && data.tags.length > 0) {
        data.tags.forEach(tag => {
          formData.append('tags[]', tag)
        })
      }

      // Add reminder times
      if (data.reminderTimes && data.reminderTimes.length > 0) {
        data.reminderTimes.forEach(time => {
          formData.append('reminderTimes[]', time.toString())
        })
      }

      // Add default reminder mode
      formData.append('defaultReminderMode', data.defaultReminderMode);
      formData.append('reminderRemarks', data.reminderRemarks || '');
      formData.append('customReminderTemplateSid', data.customReminderTemplateSid || '');

      // Add staff contact information
      if (data.staffContact) {
        if (data.staffContact.name) {
          formData.append('staffContact[name]', data.staffContact.name)
        }
        if (data.staffContact.phone) {
          formData.append('staffContact[phone]', data.staffContact.phone)
        }
      }

      // Add participants
      if (data.participants && data.participants.length > 0) {
        data.participants.forEach(userId => {
          formData.append('participants[]', userId)
        })
      } else if (data.isPrivate) {
        // For private events, always send an empty participants array to ensure proper handling
        formData.append('participants[]', '')
      }

      console.log('Form data staff contact:', data.staffContact);
      console.log('FormData entries:');
      for (const [key, value] of formData.entries()) {
        console.log(`${key}: ${value}`);
      }

      // Add image file if selected
      if (selectedImageFile) {
        formData.append('image', selectedImageFile)
      }
      
      // Add flag to remove image if user clicked remove
      if (shouldRemoveImage) {
        formData.append('removeImage', 'true')
      }

      let savedEvent: any

      if (eventId) {
        // Update existing event
        savedEvent = await eventService.updateEvent(eventId, formData)
        
        // Show appropriate success message
        if (shouldRemoveImage) {
          toast({
            title: "Event Updated Successfully",
            description: `"${savedEvent.title}" has been updated and the image has been removed.`,
            variant: "default"
          })
        } else {
          toast({
            title: "Event Updated Successfully",
            description: `"${savedEvent.title}" has been updated successfully.`,
            variant: "default"
          })
        }
      } else {
        // Create new event
        savedEvent = await eventService.createEvent(formData)
        toast({
          title: "Event Created Successfully",
          description: `"${savedEvent.title}" has been created successfully.`,
          variant: "default"
        })
      }

      // Reset image removal flag after successful save
      setShouldRemoveImage(false)
      
      // If this is a private event with participants, assign them
      if (savedEvent.isPrivate && data.participants && data.participants.length > 0) {
        try {
          console.log('Assigning participants to private event:', data.participants);
          const assignmentResult = await eventService.assignParticipants(savedEvent._id, data.participants);
          
          console.log('Participant assignment result:', assignmentResult);
          
          // Show success message with assignment summary
          if (assignmentResult.summary.assigned > 0) {
            toast({
              title: "Participants Assigned Successfully",
              description: `Successfully assigned ${assignmentResult.summary.assigned} participant(s) to the event.`,
              variant: "default"
            });
          }
          
          if (assignmentResult.summary.alreadyRegistered > 0) {
            toast({
              title: "Some Participants Already Registered",
              description: `${assignmentResult.summary.alreadyRegistered} participant(s) were already registered for this event.`,
              variant: "default"
            });
          }
          
          if (assignmentResult.summary.errors > 0) {
            toast({
              title: "Some Assignments Failed",
              description: `${assignmentResult.summary.errors} participant(s) could not be assigned. Please check the details.`,
              variant: "destructive"
            });
          }
        } catch (error) {
          console.error('Error assigning participants:', error);
          toast({
            title: "Participant Assignment Failed",
            description: "The event was created but there was an error assigning participants. You can assign them later.",
            variant: "destructive"
          });
        }
      }
      
      // Call the onSave callback with the saved event
      onSave(savedEvent)
    } catch (error: unknown) {
      console.error("Error submitting form:", error)
      
      const errorTitle = "Form submission failed"
      let errorMessage = "There was a problem submitting the form. Please try again."
      let details: string | undefined

      const axiosError = error as { response?: { status?: number; data?: { message?: string; error?: string; errors?: Record<string, string> } }; message?: string };

      if (axiosError.response?.status === 403) {
        errorMessage = "You don't have permission to create or update events."
      } else if (axiosError.response?.status === 401) {
        errorMessage = "Please log in again to continue."
      } else if (axiosError.response?.data?.message) {
        errorMessage = axiosError.response.data.message
      } else if (axiosError.message) {
        errorMessage = axiosError.message
      }
      if (axiosError.response?.data?.error) {
        details = String(axiosError.response.data.error)
      }
      if (axiosError.response?.data?.errors && typeof axiosError.response.data.errors === "object") {
        details = Object.entries(axiosError.response.data.errors).map(([k, v]) => `${k}: ${v}`).join("\n")
      }

      setSubmitErrorDialog({ open: true, title: errorTitle, message: errorMessage, details })
      toast({
        title: "Form Submission Error",
        description: errorMessage,
        variant: "destructive"
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Helper function to format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // Handle image upload
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Check file size (500KB limit to match backend)
      if (file.size > 500 * 1024) {
        const fileSizeFormatted = formatFileSize(file.size)
        const maxSizeFormatted = formatFileSize(500 * 1024)
        
        toast({
          title: "File Too Large",
          description: `Your image (${fileSizeFormatted}) exceeds the maximum size of ${maxSizeFormatted}. Please choose a smaller image or compress it before uploading.`,
          variant: "destructive"
        })
        
        // Clear the file input so user can try again
        e.target.value = ''
        return
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Invalid File Type",
          description: "Please select a valid image file (JPEG, PNG, GIF, etc.).",
          variant: "destructive"
        })
        
        // Clear the file input so user can try again
        e.target.value = ''
        return
      }

      // Show success message for valid file
      const fileSizeFormatted = formatFileSize(file.size)
      toast({
        title: "Image Selected",
        description: `Image uploaded successfully (${fileSizeFormatted}).`,
        variant: "default"
      })

      // Create preview URL
      const url = URL.createObjectURL(file)
      setPreviewImage(url)
      setSelectedImageFile(file)
      setSelectedFileInfo({ name: file.name, size: fileSizeFormatted })
      setShouldRemoveImage(false) // Reset removal flag when new image is selected
    }
  }

  // Clear image
  const clearImage = () => {
    setPreviewImage(null)
    setSelectedImageFile(null)
    setSelectedFileInfo(null)
    setShouldRemoveImage(true) // Signal that image should be removed from backend
    // Clear the file input
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement
    if (fileInput) {
      fileInput.value = ''
    }
  }

  // Helper function to get user display info
  const getUserDisplayInfo = (userId: string) => {
    const user = availableUsers.find(u => u._id === userId);
    if (!user) {
      return { name: userId, email: 'Unknown' };
    }
    
    // Safely handle potentially undefined firstName/lastName
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    const name = `${firstName} ${lastName}`.trim() || 'Unknown User';
    
    return { 
      name: name, 
      email: user.email || 'No email' 
    };
  };

  // Filter users based on search query
  const filteredUsers = availableUsers.filter(user => {
    const searchLower = userSearchQuery.toLowerCase();
    return (
      (user.firstName || '').toLowerCase().includes(searchLower) ||
      (user.lastName || '').toLowerCase().includes(searchLower) ||
      (user.email || '').toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="bg-white rounded-lg border shadow-sm p-6 w-full">
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold">{eventId ? "Edit Event" : "Create New Event"}</h2>
          <p className="text-gray-500">Fill in the details to create a new event. Fields marked with * are required.</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-8">
            <TabsTrigger value="basic">Basic Information</TabsTrigger>
            <TabsTrigger value="location">Location</TabsTrigger>
            <TabsTrigger value="sessions">Sessions</TabsTrigger>
            <TabsTrigger value="details">Details & Settings</TabsTrigger>
          </TabsList>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <TabsContent value="basic" className="space-y-6">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-medium flex items-center">
                        Event Title <span className="text-red-500 ml-1">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="Enter event title" {...field} className="h-12" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-medium flex items-center">
                        Description <span className="text-red-500 ml-1">*</span>
                      </FormLabel>
                      <FormControl>
                        <ReactQuillEditor
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Enter event description..."
                          minHeight="200px"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base font-medium flex items-center">
                          Category <span className="text-red-500 ml-1">*</span>
                        </FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="h-12">
                              <SelectValue placeholder="Select a category" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {eventCategories.map((category) => (
                              <SelectItem key={category} value={category}>
                                {category}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="targetGroup"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base font-medium flex items-center">
                          Target Group <span className="text-red-500 ml-1">*</span>
                        </FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="h-12">
                              <SelectValue placeholder="Select a target group" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {targetGroups.map((group) => (
                              <SelectItem key={group} value={group}>
                                {group}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base font-medium flex items-center">
                          Start Date <span className="text-red-500 ml-1">*</span>
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                              <CalendarIcon className="h-4 w-4 text-gray-500" />
                            </div>
                            <Input
                              type="date"
                              placeholder="Select date"
                              className="pl-10 h-12"
                              value={format(field.value, "yyyy-MM-dd")}
                              onChange={(e) => {
                                const newStartDate = new Date(e.target.value);
                                field.onChange(newStartDate);
                                // Reset endDate if it now falls before the new startDate
                                const currentEndDate = form.getValues("endDate");
                                if (currentEndDate && newStartDate > currentEndDate) {
                                  form.setValue("endDate", newStartDate, { shouldValidate: true });
                                }
                              }}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="endDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base font-medium flex items-center">
                          End Date <span className="text-red-500 ml-1">*</span>
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                              <CalendarIcon className="h-4 w-4 text-gray-500" />
                            </div>
                            <Input
                              type="date"
                              placeholder="Select date"
                              className="pl-10 h-12"
                              min={format(watchedStartDate, "yyyy-MM-dd")}
                              value={format(field.value, "yyyy-MM-dd")}
                              onChange={(e) => field.onChange(new Date(e.target.value))}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>

              <TabsContent value="location" className="space-y-6">
                <FormField
                  control={form.control}
                  name="location.onlineEvent"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel>Online Event</FormLabel>
                    </FormItem>
                  )}
                />

                {form.watch("location.onlineEvent") ? (
                  <FormField
                    control={form.control}
                    name="location.meetingLink"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base font-medium flex items-center">
                          Meeting Link <span className="text-red-500 ml-1">*</span>
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                              <Link className="h-4 w-4 text-gray-500" />
                            </div>
                            <Input
                              placeholder="Enter meeting link"
                              {...field}
                              className="pl-10 h-12"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : (
                  <>
                    <FormField
                      control={form.control}
                      name="location.venue"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-base font-medium flex items-center">
                            Venue <span className="text-red-500 ml-1">*</span>
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                <MapPin className="h-4 w-4 text-gray-500" />
                              </div>
                              <Input
                                placeholder="Enter venue name"
                                {...field}
                                className="pl-10 h-12"
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="location.address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-base font-medium flex items-center">
                            Address <span className="text-red-500 ml-1">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter full address"
                              {...field}
                              className="h-12"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="location.district"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-base font-medium flex items-center">
                            District <span className="text-red-500 ml-1">*</span>
                          </FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="h-12">
                                <SelectValue placeholder="Select a district" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {hongKongDistricts.map((district) => (
                                <SelectItem key={district} value={district}>
                                  {district}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}
              </TabsContent>

              <TabsContent value="sessions" className="space-y-6">
                <FormField
                  control={form.control}
                  name="sessions"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <EventSessions
                          sessions={field.value}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>

              <TabsContent value="details" className="space-y-6">
                <FormItem>
                  <FormLabel className="text-base font-medium">Event Cover Image</FormLabel>
                  <FormControl>
                    <div className="space-y-4">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="cursor-pointer h-12"
                      />
                      
                      {/* File info display */}
                      {selectedFileInfo && (
                        <div className="bg-green-50 border border-green-200 rounded-md p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-green-600">✅</span>
                              <span className="text-sm font-medium text-green-800">{selectedFileInfo.name}</span>
                            </div>
                            <span className="text-sm text-green-600 bg-green-100 px-2 py-1 rounded">
                              {selectedFileInfo.size}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {previewImage && (
                        <div className="relative aspect-square rounded-md overflow-hidden border">
                          <img
                            src={previewImage}
                            alt="Event preview"
                            className="object-cover w-full h-full"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="absolute top-2 right-2"
                            onClick={clearImage}
                          >
                            Remove
                          </Button>
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <div className="text-sm text-muted-foreground space-y-2">
                    <p>Upload an image for the event (optional).</p>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-blue-600">📏 File Size Limit:</span>
                      <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-md font-mono">500 KB</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-green-600">✅ Supported Formats:</span>
                      <span className="text-gray-600">JPEG, PNG, GIF, WebP</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-purple-600">📐 Recommended:</span>
                      <span className="text-gray-600">Square images (1:1 ratio) work best</span>
                    </div>
                  </div>
                </FormItem>

                <FormField
                  control={form.control}
                  name="isPrivate"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel>Private Event</FormLabel>
                      {/* 
                      <FormDescription>
                        Private events are only visible to invited participants.
                      </FormDescription>
                      */}
                      <FormDescription>
                        Private events are NOT visible to public.
                      </FormDescription>
                    </FormItem>
                  )}
                />

                {/* Participant Management for Private Events */}
                {form.watch('isPrivate') && (
                  <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
                    <h3 className="text-lg font-medium">Participant Assignment</h3>
                    <p className="text-sm text-gray-600">
                      Select users to assign as participants to this private event. These users will be automatically registered and can view the event.
                    </p>

                                             <div className="space-y-4">
                           <div>
                             <Label htmlFor="participants">Assigned Participants</Label>
                             <FormDescription>
                               Users who will be automatically registered and can view this private event
                               {!eventId && (
                                 <span className="block mt-1 text-amber-600">
                                   Note: Participants can be managed after the event is created
                                 </span>
                               )}
                             </FormDescription>
                        
                        {/* Participant Count */}
                        <div className="mt-1 text-sm text-gray-600">
                          {form.getValues('participants')?.length || 0} participant(s) selected
                        </div>

                        {/* Participant Search and Selection */}
                        <div className="mt-2 space-y-2">
                          {/* Search Input */}
                          <div className="relative">
                            <Input
                              placeholder="Search users by name or email..."
                              value={userSearchQuery}
                              onChange={(e) => setUserSearchQuery(e.target.value)}
                              className="pr-8"
                            />
                            <Search className="absolute right-2 top-2.5 h-4 w-4 text-gray-400" />
                          </div>
                          
                                                     <Select
                             onValueChange={(value) => {
                               const current = form.getValues('participants') || [];
                               if (!current.includes(value)) {
                                 form.setValue('participants', [...current, value]);
                               }
                             }}
                             disabled={loadingUsers || availableUsers.length === 0}
                           >
                             <SelectTrigger>
                               <SelectValue placeholder={
                                 loadingUsers ? "Loading users..." : 
                                 availableUsers.length === 0 ? "No users available" : 
                                 "Select participants..."
                               } />
                             </SelectTrigger>
                            <SelectContent>
                              {filteredUsers.map((user) => {
                                const isAlreadySelected = (form.getValues('participants') || []).includes(user._id);
                                return (
                                  <SelectItem 
                                    key={user._id} 
                                    value={user._id}
                                    disabled={isAlreadySelected}
                                  >
                                    {user.firstName} {user.lastName} ({user.email || 'No email'})
                                    {isAlreadySelected && " - Already selected"}
                                  </SelectItem>
                                );
                              })}
                              {filteredUsers.length === 0 && (
                                <SelectItem value="no-users" disabled>
                                  {userSearchQuery ? "No users found matching your search" : "No users available"}
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                          
                          {/* Display selected participants */}
                          <div className="mt-2 space-y-2">
                            {(form.getValues('participants') || []).map((userId, index) => {
                              const userInfo = getUserDisplayInfo(userId);
                              return (
                                <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border shadow-sm">
                                  <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                      <span className="text-blue-600 text-sm font-medium">
                                        {typeof userInfo.name === 'string' && userInfo.name.length > 0 
                                          ? userInfo.name.charAt(0).toUpperCase() 
                                          : '?'}
                                      </span>
                                    </div>
                                    <div>
                                      <p className="font-medium text-gray-900">{userInfo.name}</p>
                                      <p className="text-sm text-gray-500">{userInfo.email}</p>
                                    </div>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={async () => {
                                      try {
                                        // Only call backend if this is an existing event
                                        if (eventId) {
                                          const result = await eventService.removeParticipantRegistrations(eventId, [userId]);
                                          console.log('Participant removal result:', result);
                                          
                                          if (result.summary.removed > 0) {
                                            toast({
                                              title: "Participant Removed",
                                              description: `Successfully removed ${result.summary.removed} participant(s) from the event.`,
                                              variant: "default"
                                            });
                                          }
                                          
                                          if (result.summary.errors > 0) {
                                            toast({
                                              title: "Some Removals Failed",
                                              description: `${result.summary.errors} participant(s) could not be removed. Please check the details.`,
                                              variant: "destructive"
                                            });
                                          }
                                        }
                                        
                                        // Update the UI immediately
                                        const current = form.getValues('participants') || [];
                                        form.setValue('participants', current.filter(id => id !== userId));
                                      } catch (error) {
                                        console.error('Error removing participant:', error);
                                        toast({
                                          title: "Removal Failed",
                                          description: "Failed to remove participant from the event. Please try again.",
                                          variant: "destructive"
                                        });
                                      }
                                    }}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    Remove
                                  </Button>
                                </div>
                              );
                            })}
                            
                                                                                      {(!form.getValues('participants') || (form.getValues('participants')?.length || 0) === 0) && (
                               <div className="text-center py-4 text-gray-500">
                                 <p>No participants selected yet</p>
                                 <p className="text-sm">
                                   {!eventId ? 
                                     "Participants can be added after the event is created" : 
                                     "Use the dropdown above to add participants"
                                   }
                                 </p>
                               </div>
                             )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-medium">Event Status</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="h-12">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {eventStatuses.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="registrationFormId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-medium flex items-center">
                        Registration Form <span className="text-red-500 ml-1">*</span>
                      </FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="h-12">
                            <SelectValue placeholder={loadingForms ? "Loading forms..." : "Select a registration form"} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {loadingForms ? (
                            <SelectItem value="loading" disabled>
                              Loading forms...
                            </SelectItem>
                          ) : registrationForms.length === 0 ? (
                            <SelectItem value="no-forms" disabled>
                              No forms available
                            </SelectItem>
                          ) : (
                            registrationForms.map((form) => (
                              <SelectItem key={form._id} value={form._id}>
                                {form.title}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        {registrationForms.length === 0 && !loadingForms && "No registration forms found. Please create forms first."}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="capacity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-medium">Event Capacity</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="Enter maximum number of participants"
                          {...field}
                          onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                          className="h-12"
                        />
                      </FormControl>
                      <FormDescription>
                        Leave empty for unlimited capacity.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="reminderTimes"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <ReminderTimeConfig
                          value={field.value || []}
                          onChange={field.onChange}
                          disabled={isSubmitting}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="reminderRemarks"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel className="text-base font-medium">WhatsApp Reminder Remarks (Optional)</FormLabel>
                        <span className="text-xs text-muted-foreground">
                          {(field.value || "").length} / 200
                        </span>
                      </div>
                      <FormControl>
                        <Textarea
                          placeholder="e.g., Please bring your HKID card and wear comfortable shoes."
                          className="resize-none"
                          rows={3}
                          maxLength={200}
                          disabled={isSubmitting}
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormDescription>
                        Custom remark to be included in the automated WhatsApp reminder template (Remark: &#123;&#123;10&#125;&#125;).
                        If left blank, it will automatically default to: <em>"No special remarks for this activity. We look forward to seeing you."</em>
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="customReminderTemplateSid"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-medium">Custom WhatsApp Reminder Template SID (Optional)</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g., HXxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                          disabled={isSubmitting}
                          {...field}
                          value={field.value || ""}
                          className="font-mono text-sm"
                        />
                      </FormControl>
                      <FormDescription>
                        For special/flagship events (e.g., Annual Event) using a dedicated WhatsApp reminder template.
                        Leave blank to use the standard default reminder template.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Staff Contact Information</h3>
                  <p className="text-sm text-muted-foreground">
                    This information will be included in event notification messages for participants to contact if they have any issues.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="staffContact.name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-base font-medium">Staff Name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter staff member name"
                              {...field}
                              className="h-12"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="staffContact.phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-base font-medium">Staff Phone Number</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter phone number (e.g., +852 1234 5678)"
                              {...field}
                              className="h-12"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </TabsContent>

              <div className="flex justify-end space-x-4 pt-4">
                <Button type="button" variant="outline" onClick={onClose} className="h-12 px-6">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-black hover:bg-black/80 text-white h-12 px-6"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : eventId ? "Update Event" : "Create Event"}
                </Button>
              </div>
            </form>
          </Form>
        </Tabs>
      </div>

      <Dialog open={submitErrorDialog.open} onOpenChange={(open) => setSubmitErrorDialog((p) => ({ ...p, open }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              {submitErrorDialog.title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm">{submitErrorDialog.message}</p>
            {submitErrorDialog.details && (
              <pre className="mt-2 rounded bg-muted p-3 text-xs whitespace-pre-wrap break-words">{submitErrorDialog.details}</pre>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubmitErrorDialog((p) => ({ ...p, open: false }))}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
